import React, { useEffect, useState } from "react";
import i18n from "i18next";
import {
  LuSkipBack,
  LuPlay,
  LuPause,
  LuSparkles,
  LuCheck,
  LuAudioLines,
  LuImage,
  LuType,
  LuLayoutGrid,
  LuLock,
  LuLockOpen,
} from "react-icons/lu";
import SpeedControl from "../SpeedControl";
import { getToken } from "../../../../../../common/environment_config.es6";
import { MotionChoices } from "../../../../ai-chat/tools/MotionChoices";
import {
  initialChoice,
  DENSITIES,
  GRAPHICS,
  ICON_MODES,
} from "../../../../ai-chat/runMotionDirector";
import "./Toolbar.scss";

// "How it works" clip: the Help Hub's admin-managed promotion video for the Sync with AI guide
// (upload on /admin/promotion-videos, location "help-hub", refKey below). Failures are silent.
const HOW_IT_WORKS_REF = "sync-with-ai:overview";

function useHowItWorksVideo(open) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!open || url) return undefined;
    let cancelled = false;
    const apiUrl = process.env.MW_API_URL || window.ideaLibraryRootUrl || "";
    fetch(`${apiUrl}api/promotion-videos?location=help-hub`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((rows) => {
        const row = Array.isArray(rows) && rows.find((r) => r && r.refKey === HOW_IT_WORKS_REF);
        if (!cancelled && row && row.videoUrl) setUrl(row.videoUrl);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open, url]);
  return url;
}

function Toolbar({
  durationLocked,
  onToggleDurationLock,
  playing,
  waiting,
  currentFrame,
  duration,
  speed,
  onSpeedChange,
  maxSpeed,
  speedLimitReason,
  onDurationChange,
  collapsed,
  knollingViewVisible,
  onToggleCollapse,
  onPlay,
  onSeekStart,
  onSyncScene,
  onMotionizeScene,
  onToggleKnolling,
  syncingScene,
  syncSceneStatus,
}) {
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [syncMode, setSyncMode] = useState("sync");
  const [motionChoices, setMotionChoices] = useState(null);
  const [extractTexts, setExtractTexts] = useState(false);
  const [editingDuration, setEditingDuration] = useState(false);
  const [durationInput, setDurationInput] = useState("");
  const t = (key) => i18n.t(`messages.syncScene.${key}`);
  const howItWorksUrl = useHowItWorksVideo(syncModalOpen);

  function formatTime(frame) {
    const fps = 30;
    const totalSeconds = frame / fps;
    const m = Math.floor(totalSeconds / 60);
    const s = Math.floor(totalSeconds % 60);
    const cs = Math.floor((totalSeconds % 1) * 100);
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}.${String(cs).padStart(2, "0")}`;
  }

  const openSyncModal = () => {
    setSyncMode("sync");
    setExtractTexts(false); // paid: never carried over from an earlier opening
    // no Text choice: Enrich & Motionize always lifts the image text
    setMotionChoices({
      density: initialChoice(null, "density", DENSITIES, "key"),
      graphics: initialChoice(null, "graphics", GRAPHICS, "balanced"),
      icons: initialChoice(null, "icons", ICON_MODES, "auto"),
    });
    setSyncModalOpen(true);
  };

  const handleConfirmSync = () => {
    setSyncModalOpen(false);
    if (syncMode === "motion") onMotionizeScene?.(motionChoices);
    else onSyncScene?.({ extractTexts });
  };

  return (
    <>
      <div className="it-toolbar">
        <div className="it-toolbar__left">
          {onToggleCollapse && (
            <>
              <button
                className="it-btn it-btn--timeline-toggle"
                onClick={onToggleCollapse}
                title={collapsed ? "Timeline'ı Aç" : "Timeline'ı Kapat"}
              >
                <i
                  className="material-icons"
                  style={{
                    fontSize: "16px",
                    transition: "transform 0.25s ease",
                    transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)",
                  }}
                >
                  keyboard_arrow_down
                </i>
                Timeline
              </button>
              <div className="it-toolbar__divider" />
            </>
          )}
          <button
            className="it-btn it-btn--icon"
            onClick={onSeekStart}
            title={i18n.t("SeekToStart") || "Başa al"}
          >
            <LuSkipBack size={18} />
          </button>

          <button
            className={`it-btn it-btn--play ${playing ? "it-btn--playing" : ""} ${
              waiting ? "it-btn--waiting" : ""
            }`}
            onClick={onPlay}
            aria-label={playing ? "Pause" : "Play"}
            title={playing ? i18n.t("Pause") || "Duraklat" : i18n.t("Play") || "Oynat"}
            aria-busy={waiting || undefined}
          >
            {playing ? <LuPause size={20} /> : <LuPlay size={20} />}
          </button>
          <SpeedControl
            speed={speed}
            onChange={onSpeedChange}
            maxSpeed={maxSpeed}
            limitReason={speedLimitReason}
          />

          <div className="it-toolbar__divider" />

          <button
            className={`it-btn it-btn--sync ${syncingScene ? "it-btn--syncing" : ""} ${syncSceneStatus === "success" ? "it-btn--success" : ""}`}
            onClick={() => !syncingScene && openSyncModal()}
            disabled={syncingScene}
            title={t("aiButton")}
          >
            {syncSceneStatus === "success" ? <LuCheck size={15} /> : <LuSparkles size={15} />}
            <span>{t("aiButton")}</span>
          </button>

          <div className="it-toolbar__divider" />

          <button
            className={`it-btn it-btn--icon ${knollingViewVisible ? "it-btn--active" : ""}`}
            onClick={onToggleKnolling}
            title={i18n.t("ShowMediaAssets") || "Medya Varlıklarını Göster"}
          >
            <LuLayoutGrid size={18} />
          </button>

          <div className={`it-time-display${durationLocked ? " it-time-display--locked" : ""}`}>
            <span className="it-time-display__current">{formatTime(Math.round(currentFrame))}</span>
            <span className="it-time-display__sep">/</span>
            {editingDuration && !durationLocked ? (
              <input
                className="it-time-display__duration-input"
                value={durationInput}
                autoFocus
                onChange={(e) => setDurationInput(e.target.value)}
                onBlur={() => {
                  const parts = durationInput.split(":").map(Number);
                  let totalSec = 0;
                  if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
                    totalSec = parts[0] * 60 + parts[1];
                  } else if (parts.length === 1 && !isNaN(parts[0])) {
                    totalSec = parts[0];
                  }
                  if (totalSec > 0) {
                    onDurationChange?.(Math.round(totalSec * 30));
                  }
                  setEditingDuration(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.target.blur();
                  if (e.key === "Escape") setEditingDuration(false);
                }}
              />
            ) : (
              <span
                className="it-time-display__total it-time-display__total--editable"
                title={durationLocked ? "Süre kilitli" : "Toplam süreyi düzenlemek için tıklayın"}
                onClick={() => {
                  if (durationLocked) return;
                  const fps = 30;
                  const totalSec = duration / fps;
                  const m = Math.floor(totalSec / 60);
                  const s = Math.floor(totalSec % 60);
                  setDurationInput(`${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`);
                  setEditingDuration(true);
                }}
              >
                {formatTime(duration)}
              </span>
            )}
            <button
              className={`it-duration-lock-btn${durationLocked ? " it-duration-lock-btn--locked" : ""}`}
              title={durationLocked ? "Süreyi kilitle — tıkla açmak için" : "Süreyi kilitle"}
              onClick={onToggleDurationLock}
            >
              {durationLocked ? <LuLock size={12} /> : <LuLockOpen size={12} />}
            </button>
          </div>
        </div>
      </div>

      {syncModalOpen && (
        <div className="it-sync-overlay" onClick={() => setSyncModalOpen(false)}>
          <div className="it-sync-modal" onClick={(e) => e.stopPropagation()}>
            <div className="it-sync-modal__header">
              <div className="it-sync-modal__icon">
                <LuSparkles size={20} />
              </div>
              <h3 className="it-sync-modal__title">{t("modal.title")}</h3>
            </div>

            <div className="it-sync-modal__main">
              <div className="it-sync-modal__body">
                <div className="it-sync-modal__modes">
                  {[
                    ["sync", "modeSync", "modeSyncDesc"],
                    ["motion", "modeMotion", "modeMotionDesc"],
                  ].map(([mode, label, desc]) => (
                    <button
                      key={mode}
                      type="button"
                      data-mode={mode}
                      aria-pressed={syncMode === mode}
                      className={`it-sync-modal__mode${syncMode === mode ? " it-sync-modal__mode--on" : ""}`}
                      onClick={() => setSyncMode(mode)}
                    >
                      <strong>{t(`modal.${label}`)}</strong>
                      <span>{t(`modal.${desc}`)}</span>
                    </button>
                  ))}
                </div>
                {syncMode === "motion" ? (
                  <>
                    <div className="aidea-chat-root it-sync-modal__choices">
                      <MotionChoices
                        value={motionChoices}
                        onChange={setMotionChoices}
                        withText={false}
                      />
                    </div>
                    <p className="it-sync-modal__note">{t("modal.motionPaidNote")}</p>
                  </>
                ) : (
                  <>
                    <p className="it-sync-modal__desc">{t("modal.desc")}</p>
                    <ul className="it-sync-modal__list">
                      <li>
                        <LuAudioLines size={15} />
                        {t("modal.step1")}
                      </li>
                      <li>
                        <LuImage size={15} />
                        {t("modal.step2")}
                      </li>
                      <li>
                        <LuType size={15} />
                        {t("modal.step3")}
                      </li>
                    </ul>
                    <label className="it-sync-modal__toggle">
                      <input
                        type="checkbox"
                        role="switch"
                        data-testid="sync-extract-texts"
                        checked={extractTexts}
                        onChange={(e) => setExtractTexts(e.target.checked)}
                      />
                      <span>{t("modal.extractTexts")}</span>
                      <small>{t("modal.extractTextsPrice")}</small>
                    </label>
                  </>
                )}
              </div>
              <aside className="it-sync-modal__how">
                <h4 className="it-sync-modal__how-title">{t("modal.howItWorks")}</h4>
                <div className="it-sync-modal__how-video">
                  {howItWorksUrl ? (
                    <video
                      controls
                      controlsList="nodownload noplaybackrate noremoteplayback"
                      disablePictureInPicture
                      preload="metadata"
                      src={howItWorksUrl}
                    />
                  ) : (
                    <span>{t("modal.howItWorksSoon")}</span>
                  )}
                </div>
                <p className="it-sync-modal__how-text">
                  {t(syncMode === "motion" ? "modal.howItWorksMotion" : "modal.howItWorksSync")}
                </p>
              </aside>
            </div>

            <div className="it-sync-modal__actions">
              <button
                className="it-sync-modal__btn it-sync-modal__btn--cancel"
                onClick={() => setSyncModalOpen(false)}
              >
                {t("modal.cancel")}
              </button>
              <button
                className="it-sync-modal__btn it-sync-modal__btn--confirm"
                onClick={handleConfirmSync}
              >
                <LuSparkles size={15} />
                {t(syncMode === "motion" ? "modal.motionConfirm" : "modal.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Toolbar;
